import React, { memo, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import { PureQueryOptions } from '@apollo/client';

import { PATHS } from 'router/config';
import { useConversations, IConversation } from 'core/hooks';
import {
  useConversationMessagesListQuery,
  useConversationReplyIdentityQuery,
  useSetMsgHandledMutation,
  ConversationMessagesListDocument,
} from 'core/graphql';
import { Colors } from 'services/ColorService';

import {
  TaskFromMessageModal,
  ITaskSourceMessage,
} from './TaskFromMessageModal';
import { AuthorAvatar } from './AuthorAvatar';

interface ICommunicationsPanelProps {
  studentId?: string;
  groupId?: string;
  /** Show only Slack (`slack`) or only Gmail (`email`) conversations. */
  kindFilter?: 'slack' | 'email';
  /** Deep-link preselect — opens this conversation on mount. */
  initialConversationId?: string;
  /** Extra refetches for triage mutations (communicator badge queries). */
  extraRefetchQueries?: PureQueryOptions[];
  /** Override for the linked-task chip (default: navigate to the card's
   * Задачи tab) — the communicator switches its local tab instead. */
  onOpenTasks?: () => void;
}

/**
 * Slack conversations for a student or group (spec 0003 Phase 3): pick a
 * conversation, read the thread, reply inline. Unanswered student messages are
 * flagged. Shared by StudentDetail and GroupDetail.
 */
export const CommunicationsPanel = memo(
  ({
    studentId,
    groupId,
    kindFilter,
    initialConversationId,
    extraRefetchQueries,
    onOpenTasks,
  }: ICommunicationsPanelProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const {
      conversations,
      loading,
      replying,
      reply,
      markRead,
      setStatus,
      listRefetch,
    } = useConversations({
      studentId,
      groupId,
      extraRefetch: extraRefetchQueries,
    });
    const [selectedId, setSelectedId] = useState<string | null>(
      initialConversationId ?? null,
    );
    const [text, setText] = useState('');
    const [taskMessage, setTaskMessage] = useState<ITaskSourceMessage | null>(
      null,
    );
    const selectedConv = conversations.find((c) => c.id === selectedId);

    const visibleConversations = useMemo(
      () =>
        kindFilter
          ? conversations.filter((c) =>
              kindFilter === 'email'
                ? c.kind === 'gmail_thread'
                : c.kind !== 'gmail_thread',
            )
          : conversations,
      [conversations, kindFilter],
    );

    // Opening a conversation marks it read (team-wide); closing does nothing.
    const onSelect = useCallback(
      (conv: IConversation) => {
        const opening = conv.id !== selectedId;
        setSelectedId(opening ? conv.id : null);
        if (opening && conv.unreadCount > 0) markRead(conv.id);
      },
      [selectedId, markRead],
    );

    const {
      data: messagesData,
      loading: messagesLoading,
    } = useConversationMessagesListQuery({
      variables: { conversationId: selectedId || '' },
      skip: !selectedId,
      fetchPolicy: 'cache-and-network',
    });
    const messages = messagesData?.conversationMessages || [];

    // Who the inline reply goes out as (manager / bot / mailbox) — shown right
    // above the reply input so the sender identity is never a surprise.
    const { data: identityData } = useConversationReplyIdentityQuery({
      variables: { conversationId: selectedId || '' },
      skip: !selectedId,
      fetchPolicy: 'cache-and-network',
    });
    const identity = identityData?.replyIdentity;

    const selectedKind =
      conversations.find((c) => c.id === selectedId)?.kind || '';

    const onSend = useCallback(async () => {
      if (!selectedId || !text.trim()) return;
      const ok = await reply(selectedId, text.trim(), selectedKind);
      if (ok) setText('');
    }, [reply, selectedId, selectedKind, text]);

    // A linked-task chip navigates to the same card's "Задачи" tab — unless
    // the host page (communicator) wants to switch its own tab instead.
    const openTasksTab = useCallback(() => {
      if (onOpenTasks) {
        onOpenTasks();
        return;
      }
      if (studentId) {
        history.push(
          `${PATHS.studentDetail.replace(':id', studentId)}?tab=tasks`,
        );
      } else if (groupId) {
        history.push(`${PATHS.groupDetail.replace(':id', groupId)}?tab=tasks`);
      }
    }, [history, studentId, groupId, onOpenTasks]);

    // Mark handled without creating a task / undo a misclicked mark. The
    // mutation payload writes `isHandled` back into the cached message.
    const [setHandled] = useSetMsgHandledMutation();
    const toggleHandled = useCallback(
      (messageId: string, handled: boolean) => {
        setHandled({ variables: { messageId, handled } }).catch(() => {});
      },
      [setHandled],
    );

    if (loading && conversations.length === 0) {
      return null;
    }

    return (
      <Card>
        <CardTitle>{t('pages.communications.title')}</CardTitle>
        {visibleConversations.length === 0 ? (
          <Muted>{t('pages.communications.empty')}</Muted>
        ) : (
          <List>
            {visibleConversations.map((c) => {
              const unread = c.unreadCount > 0;
              const channel =
                c.kind === 'gmail_thread'
                  ? {
                      label: t('pages.communications.channelEmail'),
                      tone: 'email' as const,
                    }
                  : c.kind === 'slack_dm'
                  ? {
                      label: t('pages.communications.channelSlackDm'),
                      tone: 'slack' as const,
                    }
                  : {
                      label: t('pages.communications.channelChannel'),
                      tone: 'channel' as const,
                    };
              return (
                <ConvRow
                  key={c.id}
                  type="button"
                  active={c.id === selectedId}
                  onClick={() => onSelect(c)}
                >
                  <ConvMain>
                    <ConvTop>
                      <ChannelBadge tone={channel.tone}>
                        {channel.label}
                      </ChannelBadge>
                      <ConvTitle unread={unread}>{c.displayTitle}</ConvTitle>
                    </ConvTop>
                    {c.kind === 'gmail_thread' && c.mailboxEmail ? (
                      <MailboxLine>
                        {t('pages.communications.mailboxTo')}: {c.mailboxEmail}
                      </MailboxLine>
                    ) : null}
                  </ConvMain>
                  <ConvStatus>
                    {unread ? (
                      <UnreadBadge>
                        {c.unreadCount} {t('pages.communications.newShort')}
                      </UnreadBadge>
                    ) : null}
                    <StatusPill status={c.status}>
                      {t(`pages.communications.status.${c.status}`)}
                    </StatusPill>
                    {c.isUnanswered && c.status !== 'done' ? (
                      <WaitHint>
                        {t('pages.communications.waitingReply')}
                      </WaitHint>
                    ) : null}
                  </ConvStatus>
                </ConvRow>
              );
            })}
          </List>
        )}

        {selectedId ? (
          <Thread>
            <ActionBar>
              <StatusControl>
                {(['open', 'in_progress', 'done'] as const).map((s) => (
                  <StatusBtn
                    key={s}
                    type="button"
                    active={selectedConv?.status === s}
                    status={s}
                    onClick={() => setStatus(selectedId, s)}
                  >
                    {t(`pages.communications.status.${s}`)}
                  </StatusBtn>
                ))}
              </StatusControl>
              <RightActions>
                {selectedConv &&
                selectedConv.kind !== 'gmail_thread' &&
                selectedConv.teamId ? (
                  <SlackLink
                    href={`https://app.slack.com/client/${selectedConv.teamId}/${selectedConv.externalId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('pages.communications.openInSlack')}
                  </SlackLink>
                ) : null}
                <LinkAction
                  type="button"
                  onClick={() => markRead(selectedId, false)}
                >
                  {t('pages.communications.markUnread')}
                </LinkAction>
              </RightActions>
            </ActionBar>
            {messagesLoading && messages.length === 0 ? (
              <Muted>{t('pages.communications.loading')}</Muted>
            ) : (
              <Messages>
                {messages.map((m) => {
                  const outbound = m.direction === 'outbound';
                  return (
                    <MessageBlock key={m.id} outbound={outbound}>
                      <MetaRow>
                        {!outbound ? (
                          <AuthorAvatar name={m.authorDisplay} size={20} />
                        ) : null}
                        <MetaAuthor>{m.authorDisplay || '—'}</MetaAuthor>
                        <MetaTime>
                          {moment(m.sentAt).format('DD.MM.YYYY HH:mm')}
                        </MetaTime>
                      </MetaRow>
                      <Bubble outbound={outbound}>
                        <BubbleBody>{m.body}</BubbleBody>
                        {m.direction === 'inbound' ? (
                          m.linkedTask ? (
                            <BubbleTaskChip
                              type="button"
                              status={m.linkedTask.status}
                              title={m.linkedTask.title}
                              onClick={openTasksTab}
                            >
                              {t('pages.communications.taskChip')}:{' '}
                              {m.linkedTask.title}
                            </BubbleTaskChip>
                          ) : m.isHandled ? (
                            <BubbleHandled
                              type="button"
                              title={t('pages.communications.markUnhandled')}
                              onClick={() => toggleHandled(m.id, false)}
                            >
                              ✓ {t('pages.communications.handledShort')}
                            </BubbleHandled>
                          ) : (
                            <BubbleActions>
                              <BubbleTaskLink
                                type="button"
                                onClick={() =>
                                  setTaskMessage({
                                    id: m.id,
                                    body: m.body,
                                    authorName: m.authorDisplay || m.authorName,
                                    channelName:
                                      selectedConv?.displayTitle ||
                                      selectedConv?.mailboxEmail,
                                    kind: selectedConv?.kind,
                                    sentAt: m.sentAt,
                                  })
                                }
                              >
                                {t('pages.communications.createTask')}
                              </BubbleTaskLink>
                              <BubbleHandleLink
                                type="button"
                                onClick={() => toggleHandled(m.id, true)}
                              >
                                {t('pages.communications.markHandled')}
                              </BubbleHandleLink>
                            </BubbleActions>
                          )
                        ) : null}
                      </Bubble>
                    </MessageBlock>
                  );
                })}
              </Messages>
            )}
            {identity ? (
              <FromLine>
                {t('pages.communications.replyFrom', {
                  name:
                    identity.name ||
                    (identity.via === 'bot'
                      ? t('pages.communications.replyFromBotFallback')
                      : '—'),
                })}
              </FromLine>
            ) : null}
            <ReplyRow>
              <ReplyInput
                value={text}
                placeholder={t('pages.communications.replyPlaceholder')}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    onSend();
                  }
                }}
              />
              <SendButton
                type="button"
                disabled={replying || !text.trim()}
                onClick={onSend}
              >
                {t('pages.communications.send')}
              </SendButton>
            </ReplyRow>
          </Thread>
        ) : null}

        {taskMessage && selectedId ? (
          <TaskFromMessageModal
            message={taskMessage}
            onClose={() => setTaskMessage(null)}
            // Refetch the thread (message got handled + linked) and the
            // conversation list (status may flip open → in_progress).
            refetchQueries={[
              {
                query: ConversationMessagesListDocument,
                variables: { conversationId: selectedId },
              },
              ...(listRefetch ? [listRefetch] : []),
              ...(extraRefetchQueries || []),
            ]}
          />
        ) : null}
      </Card>
    );
  },
);

const Card = styled.div`
  margin-top: 16px;
  padding: 14px 16px;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 10px;
  background: ${Colors.White};
`;

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${Colors.Gray};
  margin-bottom: 10px;
`;

const Muted = styled.div`
  font-size: 13px;
  color: ${Colors.Gray};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ConvRow = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border: 1px solid
    ${({ active }) => (active ? Colors.Blue : Colors.SecondLightGray)};
  background: ${({ active }) =>
    active ? Colors.BasicBlueLight : Colors.White};
  border-radius: 8px;
  padding: 7px 10px;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  text-align: left;
`;

const ConvMain = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ConvTop = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const ChannelBadge = styled.span<{ tone: 'email' | 'slack' | 'channel' }>`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-radius: 999px;
  padding: 1px 7px;
  color: ${({ tone }) =>
    tone === 'email' ? '#1e3a8a' : tone === 'slack' ? '#5b21b6' : '#475569'};
  background: ${({ tone }) =>
    tone === 'email' ? '#dbeafe' : tone === 'slack' ? '#ede9fe' : '#f1f5f9'};
`;

const ConvTitle = styled.span<{ unread: boolean }>`
  font-weight: ${({ unread }) => (unread ? 700 : 500)};
  color: ${Colors.Black};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MailboxLine = styled.div`
  font-size: 11px;
  color: ${Colors.Gray};
`;

const ConvStatus = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const UnreadBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  padding: 1px 8px;
  white-space: nowrap;
  color: #fff;
  background: #2563eb;
`;

const StatusPill = styled.span<{ status: string }>`
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  padding: 1px 8px;
  white-space: nowrap;
  color: ${({ status }) =>
    status === 'done'
      ? '#047857'
      : status === 'in_progress'
      ? '#1e3a8a'
      : '#475569'};
  background: ${({ status }) =>
    status === 'done'
      ? '#d1fae5'
      : status === 'in_progress'
      ? '#dbeafe'
      : '#f1f5f9'};
`;

const WaitHint = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #92400e;
  white-space: nowrap;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const StatusControl = styled.div`
  display: inline-flex;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
`;

const StatusBtn = styled.button<{ active: boolean; status: string }>`
  border: 0;
  background: ${({ active, status }) =>
    !active
      ? '#fff'
      : status === 'done'
      ? '#059669'
      : status === 'in_progress'
      ? '#2563eb'
      : '#64748b'};
  color: ${({ active }) => (active ? '#fff' : '#64748b')};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  cursor: pointer;

  & + & {
    border-left: 1px solid #e2e8f0;
  }
`;

const LinkAction = styled.button`
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 12px;
  color: ${Colors.Blue};
  cursor: pointer;
`;

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SlackLink = styled.a`
  font-size: 12px;
  color: ${Colors.Blue};

  &:hover {
    text-decoration: underline;
  }
`;

const Thread = styled.div`
  margin-top: 12px;
  border-top: 1px solid ${Colors.SecondLightGray};
  padding-top: 12px;
`;

const Messages = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
  margin-bottom: 10px;
`;

const MessageBlock = styled.div<{ outbound: boolean }>`
  align-self: ${({ outbound }) => (outbound ? 'flex-end' : 'flex-start')};
  max-width: 80%;
  display: flex;
  flex-direction: column;
  align-items: ${({ outbound }) => (outbound ? 'flex-end' : 'flex-start')};
  gap: 2px;
`;

const Bubble = styled.div<{ outbound: boolean }>`
  border-radius: 10px;
  padding: 6px 10px;
  background: ${({ outbound }) =>
    outbound ? Colors.BasicBlueLight : Colors.MainBackground};
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MetaAuthor = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${Colors.Black};
`;

const MetaTime = styled.span`
  font-size: 11px;
  color: ${Colors.Gray};
`;

const BubbleBody = styled.div`
  font-size: 13px;
  color: ${Colors.Black};
  white-space: pre-wrap;
`;

const BubbleActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
`;

const BubbleTaskLink = styled.button`
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 11px;
  font-weight: 600;
  color: ${Colors.Blue};
  cursor: pointer;
  align-self: flex-start;

  &:hover {
    text-decoration: underline;
  }
`;

const BubbleHandleLink = styled(BubbleTaskLink)`
  color: #047857;
`;

const BubbleHandled = styled.button`
  border: 0;
  font: inherit;
  cursor: pointer;
  display: inline-block;
  margin-top: 4px;
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  padding: 1px 7px;
  color: #047857;
  background: #d1fae5;

  &:hover {
    text-decoration: underline;
  }
`;

const BubbleTaskChip = styled.button<{ status?: string | null }>`
  border: 0;
  font: inherit;
  cursor: pointer;
  display: inline-block;
  margin-top: 4px;
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  padding: 1px 7px;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
  color: ${({ status }) =>
    status === 'done'
      ? '#047857'
      : status === 'in_progress'
      ? '#1e3a8a'
      : '#475569'};
  background: ${({ status }) =>
    status === 'done'
      ? '#d1fae5'
      : status === 'in_progress'
      ? '#dbeafe'
      : '#f1f5f9'};
`;

const FromLine = styled.div`
  font-size: 11px;
  color: ${Colors.Gray};
  margin-bottom: 4px;
`;

const ReplyRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ReplyInput = styled.input`
  flex: 1;
  box-sizing: border-box;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
`;

const SendButton = styled.button`
  border: 1px solid ${Colors.Blue};
  background: ${Colors.Blue};
  color: ${Colors.White};
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;
